import { FC, useEffect, useRef, useState } from 'react';
import { useOutsideClickDetection } from '../../../../hooks';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { FilterItemResource } from '../../../../types/FilterTypes';
import {
  DropdownWrapper,
  Icon,
  NumberBadge,
  OptionItem,
  OptionItemInput,
  OptionItemLabel,
} from '../../../style';
import DropdownSelect from '../dropdown-select/DropdownSelect';
import SingleSelectDropdownOptions from './SingleSelectDropdownOptions';

type SingleSelectDropdownProps = {
  options: readonly FilterItemResource[];
  placeholder: string;
  value?: FilterItemResource;
  displaySelectedLabel?: boolean;
  selectedLabelPosition?: 'bottom' | 'right';
  type?: 'am-label' | 'am-dropdown';
  onChange: (values: FilterItemResource) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onUpdate?: (values: FilterItemResource) => void;
  onClick?: (values: FilterItemResource) => void;
  cssClass?: string;
  displayRightLabel?: boolean;
  rightLabel?: string;
  closeAfterSelection?: boolean;
};

const SingleSelectDropdown: FC<SingleSelectDropdownProps> = ({
  selectedLabelPosition = 'bottom',
  options,
  placeholder,
  value,
  displaySelectedLabel,
  onChange,
  onOpen,
  onClose,
  onUpdate,
  onClick,
  type,
  cssClass = '',
  displayRightLabel = false,
  rightLabel = '',
  closeAfterSelection = false,
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<FilterItemResource>(
    value || { key: 'All', label: 'All' } || { key: 'Primary', label: 'Primary' },
  );
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const { isMobileDevice } = useCheckDevice();

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const isSelected = (item: FilterItemResource) => {
    return selectedValue?.key === item.key;
  };

  useEffect(() => {
    if (value) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleItemChange = (item: FilterItemResource): void => {
    setSelectedValue(item);
    if (selectedValue?.key !== item.key) {
      const newItem = { ...item };
      onChange(newItem);
    } else {
      onClick && onClick(item);
    }
    if (closeAfterSelection) {
      setIsVisible(false);
    }
  };

  const handleMouseEvent = (event: any): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  const generateOptions = (): JSX.Element[] => {
    return options?.map((item) => (
      <OptionItem
        selected={item.key === selectedValue?.key}
        key={item.key}
        onClick={() => handleItemChange(item)}
      >
        <span className='align-item'>
          <OptionItemInput
            type='radio'
            checked={isSelected(item)}
            onChange={() => handleItemChange(item)}
          />
          <OptionItemLabel>{item.label}</OptionItemLabel>
        </span>
        <span className='filter-right'>
          {!!item.key && <NumberBadge>{item.count}</NumberBadge>}
        </span>
      </OptionItem>
    ));
  };

  const getLabel = (): string => {
    if (displayRightLabel && !displaySelectedLabel) {
      return rightLabel;
    } else {
      return selectedValue?.label;
    }
  };

  const labelProps =
    displaySelectedLabel || displayRightLabel
      ? selectedLabelPosition === 'bottom'
        ? { bottomLabel: getLabel() }
        : { inlineLabel: getLabel() }
      : '';

  const handleDropdownWrapperClick = (): void => {
    setIsVisible(!isVisible);

    if (isVisible) {
      return;
    }

    if (options?.length === 0 && onUpdate) {
      onUpdate(null);
      return;
    }

    if (!options?.find((x) => x.key === selectedValue?.key) && onUpdate) {
      onUpdate(null);
      return;
    }
  };

  useEffect(() => {
    if (value === null) {
      if (placeholder === 'Vehicle Type') {
        setSelectedValue({ key: 'All', label: 'All' });
      } else if (placeholder === 'Replacement Selection') {
        setSelectedValue({ key: 'Primary', label: 'Primary' });
      } else if (placeholder === 'Website Activity') {
        setSelectedValue({ key: 'None', label: 'None' });
      }
    }
  }, [value, placeholder]);

  useEffect(() => {
    if (isVisible && onOpen) {
      onOpen();
    }
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible]);

  return (
    <DropdownWrapper
      isMobileDevice={isMobileDevice}
      data-testid='single-select-dropdown'
      ref={wrapperRef}
      flexDirection='row'
      className={cssClass}
    >
      <div className={`heap-filter-item ${type === 'am-dropdown' ? 'w-88' : ''}`}>
        <span
          data-testid='single-select-dropdown-header'
          onClick={handleDropdownWrapperClick}
          onMouseDown={handleMouseEvent}
        >
          <DropdownSelect name={placeholder} {...labelProps} />
        </span>
        {isVisible && (
          <SingleSelectDropdownOptions options={generateOptions()} filterHeight={filterHeight} />
        )}
      </div>
      {type === 'am-dropdown' && (
        <div className='vertical-center'>
          <Icon type='dropdown' size={14} />
        </div>
      )}
    </DropdownWrapper>
  );
};

export default SingleSelectDropdown;
